/**
 * THE CALL SCRIPT: what a call-eval run says, read off an eval journey (plan §12.3).
 *
 * A journey is a transcript: the caller's turns and the answers expected of the hub. On a call,
 * each user turn is a CALLER LINE the runner speaks, in order, and the assistant turns that follow
 * it are the ANSWER the hub owes that line. From each line the runner derives, deterministically:
 * - whether it expects a DELEGATION: its expected answer states a figure (an amount, a date, a
 *   code…). The voice may state no figure a delegation did not hand it, so a line whose answer
 *   carries one must reach the hub's agent; a greeting or a thank-you need not;
 * - the FACTS its answer must carry: every figure in the expected answer;
 * - the value the voice must READ BACK: an identifier the caller dictates (a CPF, a code of five
 *   digits or more).
 *
 * A PLAN file overrides any of these per line, and adds what a transcript cannot say: that a line
 * INTERRUPTS the voice's previous answer, and which pre-recorded CLIP speaks it. Lines are named
 * by their 1-based position among the caller's lines:
 *
 *   { "lines": { "2": { "interrupt": true }, "3": { "readback": "123.456.789-09", "clip": "clips/3.wav" },
 *                "5": { "expect_delegation": false } } }
 */

import type { JourneyResponse } from '@wayai/core/contracts';
import { digitRuns, extractFacts, type Fact, type FactKind } from './facts.js';

/** The journey fields the script reads (`GET /api/evals/journeys` or `…/journeys/:id`). */
export type CallJourney = Pick<JourneyResponse, 'journey_id' | 'journey_name' | 'transcript'>;

export interface CallLine {
  /** 1-based position among the journey's caller lines. */
  n: number;
  /** What the caller says: the journey's user turn. */
  say: string;
  /** The answer the hub owes the line: the journey's assistant turns after it, joined. */
  expected_reply: string;
  expect_delegation: boolean;
  /** Every figure the answer must carry. */
  expected_facts: Fact[];
  /** A value the voice must read back, as a fact kind and its canonical key; null when none. */
  readback: { kind: FactKind; key: string } | null;
  /** Start speaking while the voice is still saying the previous answer (a barge-in). */
  interrupt: boolean;
  /** A pre-recorded clip for the line (a path, as the plan names it); null for the default source. */
  clip: string | null;
}

export interface CallScript {
  journey_id: string;
  journey_name: string;
  lines: CallLine[];
}

export interface CallPlanLine {
  interrupt?: boolean;
  expect_delegation?: boolean;
  /** A value the voice must read back; `null` checks none. */
  readback?: string | null;
  clip?: string;
}

export interface CallPlan {
  lines: Record<string, CallPlanLine>;
}

/** Every field a plan line may carry: typed against `CallPlanLine`, so the two cannot drift. */
const PLAN_LINE_KEYS = new Set(Object.keys({
  interrupt: true, expect_delegation: true, readback: true, clip: true,
} satisfies Record<keyof CallPlanLine, true>));

/** Parse a plan file's JSON. Throws an `Error` naming the first thing wrong with it. */
export function parseCallPlan(raw: unknown): CallPlan {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('A call plan is a JSON object with a "lines" object.');
  const lines = (raw as { lines?: unknown }).lines ?? {};
  if (!lines || typeof lines !== 'object' || Array.isArray(lines)) throw new Error('The call plan\'s "lines" must be an object keyed by line number.');
  const out: Record<string, CallPlanLine> = {};
  for (const [key, value] of Object.entries(lines as Record<string, unknown>)) {
    if (!/^[1-9]\d*$/.test(key)) throw new Error(`Call plan line "${key}" is not a line number (1, 2, …).`);
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Call plan line ${key} must be an object.`);
    const entry = value as Record<string, unknown>;
    for (const field of Object.keys(entry)) {
      if (!PLAN_LINE_KEYS.has(field)) throw new Error(`Call plan line ${key} has an unknown field "${field}".`);
    }
    if (entry.interrupt !== undefined && typeof entry.interrupt !== 'boolean') throw new Error(`Call plan line ${key}: "interrupt" must be true or false.`);
    if (entry.expect_delegation !== undefined && typeof entry.expect_delegation !== 'boolean') {
      throw new Error(`Call plan line ${key}: "expect_delegation" must be true or false.`);
    }
    if (entry.readback !== undefined && entry.readback !== null && typeof entry.readback !== 'string') {
      throw new Error(`Call plan line ${key}: "readback" must be a string or null.`);
    }
    if (entry.clip !== undefined && (typeof entry.clip !== 'string' || !entry.clip)) throw new Error(`Call plan line ${key}: "clip" must be a path.`);
    out[key] = entry as CallPlanLine;
  }
  return { lines: out };
}

/** The identifier a line dictates, when it dictates one: a CPF, or a code of five digits or more. */
export function dictatedIdentifier(text: string): { kind: FactKind; key: string } | null {
  const fact = extractFacts(text).find((f) => f.kind === 'cpf' || (f.kind === 'digits' && f.key.length >= 5));
  if (fact) return { kind: fact.kind, key: fact.key };
  // Dictated in pieces ("um dois três, quatro cinco seis, …").
  const run = digitRuns(text).find((r) => r.length >= 5);
  return run ? { kind: run.length === 11 ? 'cpf' : 'digits', key: run } : null;
}

function readbackOf(value: string): { kind: FactKind; key: string } {
  const read = dictatedIdentifier(value) ?? (() => {
    const fact = extractFacts(value)[0];
    return fact ? { kind: fact.kind, key: fact.key } : null;
  })();
  if (!read) throw new Error(`The read-back value "${value}" holds no figure to compare.`);
  return read;
}

/**
 * The journey as a call script. Throws when the journey gives the caller nothing to say, or the
 * plan names a line the journey does not have.
 */
export function buildCallScript(journey: CallJourney, plan: CallPlan = { lines: {} }): CallScript {
  const lines: CallLine[] = [];
  let current: { say: string; replies: string[] } | null = null;
  const flush = () => {
    if (!current) return;
    const n = lines.length + 1;
    const expectedReply = current.replies.join('\n').trim();
    const expectedFacts = extractFacts(expectedReply);
    const override = plan.lines[String(n)] ?? {};
    const readback = override.readback === undefined
      ? dictatedIdentifier(current.say)
      : override.readback === null ? null : readbackOf(override.readback);
    lines.push({
      n,
      say: current.say,
      expected_reply: expectedReply,
      expect_delegation: override.expect_delegation ?? expectedFacts.length > 0,
      expected_facts: expectedFacts,
      readback,
      interrupt: override.interrupt ?? false,
      clip: override.clip ?? null,
    });
    current = null;
  };
  for (const turn of journey.transcript) {
    const content = (turn.content ?? '').trim();
    if (turn.role === 'user' && content) {
      flush();
      current = { say: content, replies: [] };
    } else if (turn.role === 'assistant' && content && current) {
      current.replies.push(content);
    }
  }
  flush();

  if (lines.length === 0) throw new Error(`Journey "${journey.journey_name}" has no caller line to say.`);
  if (lines[0].interrupt) throw new Error('Line 1 cannot interrupt: the voice has said nothing to interrupt yet.');
  for (const key of Object.keys(plan.lines)) {
    if (Number(key) > lines.length) throw new Error(`The call plan names line ${key}, but the journey has ${lines.length} caller line${lines.length === 1 ? '' : 's'}.`);
  }
  return {
    journey_id: journey.journey_id,
    journey_name: journey.journey_name,
    lines,
  };
}
